import {
	IsBrazil,
	PageWithCompliance,
	PageWithNavigation
} from "@components/templates"
import { useAllowedTools } from "@hooks/useAllowedTools"
import { useGetCruiseData } from "@hooks/useGetCruiseData"
import { useUserStore } from "@hooks/useUserStore"
import useValidateLegacySession from "@hooks/useValidateLegacySession"
import { getComplianceStatus } from "@services/complianceService"
import {
	COMPLIANCE_INTENDED_PATH_STORAGE_KEY,
	DEFAULT_INTENDED_PATH,
	ENABLE_COMPLIANCE_CHECK,
	INTENDED_PATH_STORAGE_KEY,
	SOFT_COMPLIANCE_CHECK_CUTOFF_DATE,
	SOFT_COMPLIANCE_CHECK_MODAL_SHOWN_KEY,
	SOFT_COMPLIANCE_INTENDED_PATH_STORAGE_KEY
} from "@utils/constants.util"
import { isLegacyUser } from "@utils/legacyUser.util"
import { getRouteToolCodes } from "@utils/permission.util"
import { useEffect, useMemo } from "react"
import { Navigate, Outlet } from "react-router-dom"

export const AuthenticatedRoute = () => {
	const { isToolAllowed, isLoading: isToolsLoading } = useAllowedTools()

	const { isLoading, isAuthenticated, isLoggingOut, location, userProfile } =
		useValidateLegacySession()
	const extendUserProfile = useUserStore((state) => state.extendUserProfile)

	const cruiseDataParams = userProfile
		? {
				agencyId: userProfile.agencyId,
				countryCode: userProfile.countryCode,
				currencyCode: userProfile.primaryCurrency,
				internationalRepFlag: userProfile.internationalRepFlag
			}
		: null

	const { data: cruiseData } = useGetCruiseData(cruiseDataParams)

	useEffect(() => {
		if (cruiseData) {
			extendUserProfile(cruiseData)
		}
	}, [cruiseData, extendUserProfile])

	const isCompliancePage = location.pathname.startsWith("/account-compliance")
	const compliance = useMemo(
		() => (userProfile ? getComplianceStatus(userProfile) : null),
		[userProfile]
	)

	if (isLoading) return null
	//if (isLoading || isToolsLoading) return null
	if (!isAuthenticated) {
		if (!isLoggingOut) {
			const currentPath = location.pathname + location.search + location.hash
			if (!currentPath.startsWith("/logout")) {
				sessionStorage.setItem(INTENDED_PATH_STORAGE_KEY, currentPath)
			}
		}
		return <Navigate to="/login" replace state={{ from: location }} />
	}

	if (ENABLE_COMPLIANCE_CHECK && !isCompliancePage && userProfile === null) {
		return null
	}

	if (
		ENABLE_COMPLIANCE_CHECK &&
		!isCompliancePage &&
		compliance &&
		!compliance.compliant
	) {
		const intendedPath =
			location.pathname === "/"
				? DEFAULT_INTENDED_PATH
				: location.pathname + location.search
		sessionStorage.setItem(COMPLIANCE_INTENDED_PATH_STORAGE_KEY, intendedPath)
		return <Navigate to="/account-compliance" replace />
	}

	if (
		SOFT_COMPLIANCE_CHECK_CUTOFF_DATE &&
		!ENABLE_COMPLIANCE_CHECK &&
		userProfile &&
		isLegacyUser(userProfile) &&
		compliance &&
		!compliance.compliant &&
		!sessionStorage.getItem(SOFT_COMPLIANCE_CHECK_MODAL_SHOWN_KEY) &&
		location.pathname !== "/home" &&
		!location.pathname.startsWith("/profile/security")
	) {
		const intendedPath = location.pathname + location.search
		sessionStorage.setItem(
			SOFT_COMPLIANCE_INTENDED_PATH_STORAGE_KEY,
			intendedPath
		)
		return <Navigate to="/home" replace />
	}

	if (isToolsLoading) return null
	const permissionToolCodes = getRouteToolCodes(location.pathname)

	// Secured only routes don't have permission tool codes (skip permission check)
	// If there are permission tool codes, check if the user has all the required permissions
	if (permissionToolCodes) {
		const hasPermission = permissionToolCodes.every(isToolAllowed)
		if (!hasPermission) {
			return <Navigate to="/home" replace />
		}
	}

	if (isCompliancePage) {
		return (
			<PageWithCompliance>
				<Outlet />
			</PageWithCompliance>
		)
	}

	return (
		<IsBrazil>
			<PageWithNavigation>
				<Outlet />
			</PageWithNavigation>
		</IsBrazil>
	)
}

export default AuthenticatedRoute
